import { cn } from '@/utilities/ui'

/**
 * PONTINHO PULSANTE ao lado do rótulo de um item de menu (pedido do dono, 19/08/2026): destacar um
 * item novo sem precisar mexer no texto dele.
 *
 * ── DUAS CAMADAS, NÃO UMA ──
 * O anel externo (`animate-ping`) expande e some; o miolo fica parado e sólido. Com uma camada só,
 * o ponto inteiro sumiria em cada ciclo e o item pareceria piscar defeituoso — este desenho é o
 * mesmo que o Tailwind documenta para indicador de estado, e é o que os apps usam para «novo».
 *
 * ── ACESSIBILIDADE ──
 * `motion-reduce:animate-none` desliga a animação para quem pediu menos movimento no sistema
 * operativo. O ponto continua visível e sólido — a informação («há algo novo aqui») não se perde,
 * só o movimento. Animação infinita é exatamente o caso que a WCAG 2.2.2 cobre.
 *
 * `aria-hidden` porque é decoração: quem usa leitor de tela ouve o rótulo do item, e um «ponto»
 * anunciado no meio do menu seria ruído. Se um dia o ponto passar a significar algo que só ele diz
 * (ex.: «3 não lidos»), aí precisa de texto acessível — hoje não diz.
 *
 * ── COR ──
 * VERMELHO, por decisão do dono (22/08/2026). Antes era `bg-primary` — o destaque do tema — com o
 * argumento de que vermelho fixo ficaria estridente no tema claro e apagado no escuro. O argumento
 * continua válido, e é por isso que aqui NÃO há um vermelho só: `red-500` no claro e `red-400` no
 * escuro, que é meio tom acima e mantém o ponto legível sobre fundo escuro sem berrar no claro.
 */
export function MenuPulseDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-menu-pulse-dot=""
      className={cn('relative ml-1.5 inline-flex h-1.5 w-1.5 shrink-0', className)}
    >
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500/70 opacity-75 motion-reduce:animate-none dark:bg-red-400/70" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500 dark:bg-red-400" />
    </span>
  )
}
